// Theme (§17 Theming & brand).
//
// Renderer-agnostic Theme shape. Mirrors the brand_kit DB table but in an
// ergonomic form for the synthesis pipeline. The compiler (Phase B) reads
// from this; the brand_kit table is the persistence layer.
//
// RESOLUTION CASCADE (locked):
//
//   engineDefaultTheme  →  brandKit (org)  →  themeOverrides (per-video)
//                                                       ↓
//                                              resolved Theme baked into
//                                              RenderDoc.globals.theme
//
// Each layer overrides the prior; missing layers fall through.
//
// THEME IS A SYNTHESIS INPUT, NOT EMBEDDED IN TEMPLATEDOC. TemplateDocs cite
// roles + tokens (display/heading/body/caption + font tokens). The synthesizer
// multiplies TemplateDoc × Theme → on-brand RenderDoc. The renderer never
// looks up a Theme — it reads the resolved type-styles baked into RenderDoc.
import { z } from 'zod';
import { motionTrackSchema } from './motion-schema.js';
import { ANIMATION_TOKENS } from './registries/animation-tokens.js';
import { fontRef } from './registries/font-tokens.js';
// ── Theme typeStyle override ────────────────────────────────────────
// A Theme can override font choice, weight, case, and tracking per type-style
// role. Size + color stay the engine's responsibility (size comes from the
// engine's per-role default; color comes from the colour role binding).
export const themeTypeStyleSchema = z.object({
    fontRef,
    weight: z.number().int().min(100).max(900),
    case: z.enum(['upper', 'normal']),
    /** em units. */
    tracking: z.number(),
});
// ── Theme colours ───────────────────────────────────────────────────
// Semantic colour roles. Hex strings (e.g. '#0066FF').
const hexColor = z
    .string()
    .regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/, 'expected hex colour');
export const themeColorsSchema = z.object({
    primary: hexColor,
    secondary: hexColor,
    accent: hexColor,
    surface: hexColor,
    onSurface: hexColor,
    onPrimary: hexColor,
    muted: hexColor,
});
// ── Theme logos ─────────────────────────────────────────────────────
export const themeLogoSchema = z.object({
    light: z.string().url().nullable(),
    dark: z.string().url().nullable(),
    /**
     * The colour the mark SITS ON, `#rrggbb`, or null when it sits on nothing.
     *
     * Most artwork is not see-through — 49 of 60 production marks are opaque — so
     * a mark drawn onto a dark plate arrives as a light BOX with a wordmark in
     * it. A surface that adopts this colour has no edge to show, and the box
     * disappears without touching the artwork.
     *
     * Null is the good case and the safe default: a transparent mark, or a border
     * that did not agree on one colour. A surface then keeps its own background,
     * which is the behaviour that predates this.
     */
    scrim: z.string().nullable().default(null),
    /**
     * How LIGHT the mark's ink is — WCAG relative luminance 0..1, or null when
     * unprobed.
     *
     * The half `scrim` cannot answer. A transparent mark brings no background, so
     * a scrim check finds nothing wrong with it — and dark ink on a dark plate is
     * still not there. 3 of 60 production marks are exactly this.
     */
    inkLuminance: z.number().nullable().default(null),
    /**
     * The share of the mark's ink that is DARK, 0..1 — the number the plate
     * decision is made on. `inkLuminance` is a mean, and a mean describes a mark
     * that is one tone; ClearSkin4u's averages to a comfortable 0.207 while two
     * thirds of it is invisible on a dark card.
     */
    inkDarkShare: z.number().nullable().default(null),
    /** The artwork's mean colour — an averaged stand-in for a scrim. */
    meanColor: z.string().nullable().default(null),
    /**
     * Whether the mark is see-through. Gates `meanColor`: on a transparent mark
     * the mean is the INK, and a plate painted that colour hides it entirely.
     */
    usesTransparency: z.boolean().nullable().default(null),
    /** The artwork's width / height. Gates whether it can be CONTINUED as a surface. */
    aspect: z.number().nullable().default(null),
    /**
     * The artwork's BACKGROUND — its dominant colour when one dominates, else its
     * mean. What a plate behind the mark should take, and NOT the same as
     * `meanColor`: a white card with marks on it averages to something that is not
     * white, and a plate painted that average makes the rectangle more visible
     * rather than less.
     */
    backgroundColor: z.string().nullable().default(null),
});
// ── Theme identity ──────────────────────────────────────────────────
export const themeIdentitySchema = z.object({
    businessName: z.string().min(1),
    tagline: z.string().nullable(),
    address: z.string().nullable(),
    ctaText: z.string().min(1),
    bookingUrl: z.string().url().nullable(),
    currency: z.string().min(3).max(3),
});
// ── Theme music ─────────────────────────────────────────────────────
export const themeMusicSchema = z.object({
    preferredMoods: z.array(z.string()),
});
// ── Top-level Theme ─────────────────────────────────────────────────
// ── Theme motion override (§6) ──────────────────────────────────────
//
// A brand retunes what a token MEANS without forking the engine: a calm clinic
// wants `pop` to be a gentle 0.95→1, a gym wants a stamp. The built-in
// `MOTION_PRESETS` stays mandatory — `engineDefaultTheme` must work with no
// brand — and this is a per-token override on top.
//
// Resolved at PHASE B, like every other Theme lookup, so the RenderDoc carries
// concrete tracks and a stored doc replays identically after a brand retunes.
//
// THE RISK, AND WHAT ANSWERS IT. This field lets a brand break its own videos:
// a `pop` retuned to a 90-frame animation on a 30-frame element is a
// half-finished entrance. That is not caught by validating the theme in the
// abstract — the element window is not knowable there. It is caught by motion
// lint rule §9.3 at the point of USE, where the compiler knows both the
// retuned tracks and the element they landed on, and reports
// `window-outside-element`. That is why this field did not ship until the
// lint existed, and why it must not be moved to render time.
export const themeMotionSchema = z.record(z.enum(ANIMATION_TOKENS), z.array(motionTrackSchema));
export const themeSchema = z.object({
    colors: themeColorsSchema,
    logo: themeLogoSchema,
    typeStyles: z.object({
        display: themeTypeStyleSchema,
        heading: themeTypeStyleSchema,
        body: themeTypeStyleSchema,
        caption: themeTypeStyleSchema,
    }),
    identity: themeIdentitySchema,
    music: themeMusicSchema,
    tone: z.string().nullable(),
    /** Per-token motion retune. Absent on every theme today. */
    motion: themeMotionSchema.optional(),
});
export const themeOverridesSchema = z.object({
    colors: themeColorsSchema.partial().optional(),
    logo: themeLogoSchema.partial().optional(),
    typeStyles: z
        .object({
        display: themeTypeStyleSchema.partial().optional(),
        heading: themeTypeStyleSchema.partial().optional(),
        body: themeTypeStyleSchema.partial().optional(),
        caption: themeTypeStyleSchema.partial().optional(),
    })
        .partial()
        .optional(),
    identity: themeIdentitySchema.partial().optional(),
    music: themeMusicSchema.partial().optional(),
    tone: z.string().nullable().optional(),
    motion: themeMotionSchema.optional(),
});
// ── engineDefaultTheme ──────────────────────────────────────────────
// The fallback the compiler uses when no brand_kit exists for an org. Also
// the bottom of the resolution cascade — any theme value the brand_kit /
// per-video overrides don't supply comes from here.
//
// Choices mirror wave 3's `getTypeStyle` engine fallback so existing rendered
// videos look identical when no brand_kit row exists.
export const engineDefaultTheme = {
    colors: {
        primary: '#000000',
        secondary: '#666666',
        accent: '#0066FF',
        surface: '#FFFFFF',
        onSurface: '#000000',
        onPrimary: '#FFFFFF',
        muted: '#999999',
    },
    logo: {
        light: null,
        dark: null,
        scrim: null,
        inkLuminance: null,
        inkDarkShare: null,
        meanColor: null,
        usesTransparency: null,
        aspect: null,
        backgroundColor: null,
    },
    typeStyles: {
        display: {
            fontRef: 'playfair',
            weight: 700,
            case: 'upper',
            tracking: -0.02,
        },
        heading: {
            fontRef: 'inter',
            weight: 700,
            case: 'normal',
            tracking: -0.01,
        },
        body: {
            fontRef: 'inter',
            weight: 500,
            case: 'normal',
            tracking: 0,
        },
        caption: {
            fontRef: 'inter',
            weight: 500,
            case: 'upper',
            tracking: 0.02,
        },
    },
    identity: {
        businessName: 'Untitled Business',
        tagline: null,
        address: null,
        ctaText: 'Book now',
        bookingUrl: null,
        currency: 'USD',
    },
    music: {
        preferredMoods: [],
    },
    tone: null,
};
// ── Theme merge helper ──────────────────────────────────────────────
// Deep merge: each later layer overrides the previous one. Used by
// get-resolved-theme. Theme is shallow enough that a hand-written merge is
// clearer than pulling in lodash.
export function mergeTheme(base, overrides) {
    return {
        colors: { ...base.colors, ...(overrides.colors ?? {}) },
        logo: { ...base.logo, ...(overrides.logo ?? {}) },
        typeStyles: {
            display: {
                ...base.typeStyles.display,
                ...(overrides.typeStyles?.display ?? {}),
            },
            heading: {
                ...base.typeStyles.heading,
                ...(overrides.typeStyles?.heading ?? {}),
            },
            body: { ...base.typeStyles.body, ...(overrides.typeStyles?.body ?? {}) },
            caption: {
                ...base.typeStyles.caption,
                ...(overrides.typeStyles?.caption ?? {}),
            },
        },
        identity: { ...base.identity, ...(overrides.identity ?? {}) },
        music: { ...base.music, ...(overrides.music ?? {}) },
        tone: overrides.tone === undefined ? base.tone : overrides.tone,
    };
}
// ── Contrast lint helper ────────────────────────────────────────────
// Pure function — no deps. Computes the WCAG-style contrast ratio between
// two hex colours. Used by synthesis to surface a structured error when a
// Theme's primary/onPrimary pair is unreadable.
function hexToRgb(hex) {
    const m = hex.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/);
    if (!m)
        return null;
    let v = m[1];
    if (!v)
        return null;
    if (v.length === 3) {
        const v0 = v[0] ?? '';
        const v1 = v[1] ?? '';
        const v2 = v[2] ?? '';
        v = v0 + v0 + v1 + v1 + v2 + v2;
    }
    if (v.length === 8)
        v = v.slice(0, 6); // ignore alpha for luminance
    const r = Number.parseInt(v.slice(0, 2), 16);
    const g = Number.parseInt(v.slice(2, 4), 16);
    const b = Number.parseInt(v.slice(4, 6), 16);
    return { r, g, b };
}
function relativeLuminance({ r, g, b, }) {
    const channel = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
export function contrastRatio(hexA, hexB) {
    const a = hexToRgb(hexA);
    const b = hexToRgb(hexB);
    if (!a || !b)
        return 0;
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);
    return (lighter + 0.05) / (darker + 0.05);
}
// Legacy advisory threshold. Video generation does not reject brand colours
// based on contrast.
export const MIN_PRIMARY_CONTRAST = 3.0;
export const DERIVED_COLOR_ROLES = [
    'onAccent',
    'accentDeep',
    'onLogoScrim',
];
/**
 * The fail-safe ink for a colour we cannot parse. Slate for BOTH derived
 * roles: cream (v1's choice for `onAccent`) is a light colour, and answering
 * "I don't know what this accent is" with light ink is how you get invisible
 * copy. See onAccentColor.
 */
const DERIVED_SLATE = '#15161C';
const to2 = (n) => Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, '0');
/**
 * Normalises a CSS colour to six hex digits (no `#`).
 *
 * Handles `#rgb`, `#rrggbb`, `#rrggbbaa` and — since a brand_kit accent is
 * whatever a colour picker produced — `rgb()` / `rgba()`. Alpha is discarded:
 * these functions ask "how light is this ink", and an accent is painted on an
 * opaque surface by the time it matters.
 *
 * Anything else returns null, and the CALLER decides the fallback. That is the
 * important part: the old fallback was cream, a LIGHT colour, so an accent
 * this could not parse produced light-on-light — invisible copy, and worse in
 * v2 than v1 because `resolveColorRole` exposes `onAccent` at every binding
 * site rather than one palette.
 */
function toSixHex(color) {
    const raw = color.trim();
    const rgb = /^rgba?\(([^)]+)\)$/i.exec(raw);
    if (rgb) {
        const parts = rgb[1].split(/[,\s/]+/).filter(Boolean);
        if (parts.length < 3)
            return null;
        const channels = parts.slice(0, 3).map((part) => {
            const pct = part.endsWith('%');
            const n = Number.parseFloat(part);
            if (!Number.isFinite(n))
                return Number.NaN;
            return pct ? (n / 100) * 255 : n;
        });
        if (channels.some((c) => Number.isNaN(c)))
            return null;
        return channels.map(to2).join('');
    }
    const hex = raw.replace('#', '');
    const full = hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex.length === 8
            ? hex.slice(0, 6)
            : hex;
    return /^[0-9a-fA-F]{6}$/.test(full) ? full : null;
}
/** v1's perceptual luminance (0–255), NOT the WCAG relative luminance. */
function perceivedLuminance(full) {
    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b;
}
/**
 * A readable text colour for content sitting ON `accent`. Light accents get
 * slate, dark accents get white.
 *
 * UNPARSEABLE INPUT FAILS SAFE TO SLATE, not cream. The old cream fallback was
 * a LIGHT colour used as the "I don't know" answer, so any accent this could
 * not read — an `rgb()` string, a named colour, an empty field — rendered
 * light copy that is invisible on the light surfaces those same brands tend to
 * use. Dark-on-unknown is legible on the overwhelming majority of accents;
 * light-on-unknown is legible on almost none. When in doubt, go dark.
 */
/**
 * The ink that reads best on `background` — whichever of slate or white wins.
 *
 * WHY THIS AND NOT `onAccentColor`. That one chooses by PERCEIVED luminance
 * (0.299/0.587/0.114) and is pinned to v1's numbers so ported templates resolve
 * to the hex v1 drew. Contrast, though, is judged by WCAG RELATIVE luminance,
 * and the two disagree near the boundary — a colour can be "light" to one and
 * fail the other. Choosing with the metric that will later be CHECKED means the
 * check cannot fail, rather than nearly cannot.
 *
 * Maximising rather than thresholding for the same reason: there is no cutoff to
 * get wrong, and for any background at least one of the two clears 3:1 by a
 * wide margin.
 */
export function readableInkOn(background) {
    const slate = contrastRatio(background, DERIVED_SLATE);
    const white = contrastRatio(background, '#FFFFFF');
    return white >= slate ? '#FFFFFF' : DERIVED_SLATE;
}
export function onAccentColor(accent) {
    const full = toSixHex(accent);
    if (!full)
        return DERIVED_SLATE;
    return perceivedLuminance(full) > 150 ? DERIVED_SLATE : '#FFFFFF';
}
/**
 * `accent`, darkened until it works as SMALL text on a light chip. Anything
 * lighter than mid-luminance is blended 65% toward slate so the hue survives
 * but the copy reads. Unparseable input falls back to slate.
 */
export function deepAccentColor(accent) {
    const full = toSixHex(accent);
    if (!full)
        return DERIVED_SLATE;
    if (perceivedLuminance(full) <= 105)
        return `#${full}`;
    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);
    const mix = (c, t) => Math.round(c * 0.35 + t * 0.65);
    return `#${to2(mix(r, 21))}${to2(mix(g, 22))}${to2(mix(b, 28))}`;
}
/**
 * Resolves any colour-role name — the seven authored Theme roles plus the two
 * derived ones — to a concrete colour. The ONE place a role becomes a hex, so
 * text, Frame backgrounds and bar fills can never disagree.
 */
export function resolveColorRole(role, theme) {
    if (role === 'onAccent')
        return onAccentColor(theme.colors.accent);
    if (role === 'accentDeep')
        return deepAccentColor(theme.colors.accent);
    if (role === 'onLogoScrim')
        return onLogoScrimColor(theme);
    return theme.colors[role];
}
/**
 * The ink for a surface that ADOPTED the logo's scrim — see
 * `Frame.adoptLogoScrim`.
 *
 * Adopting the scrim is how an opaque mark stops reading as a box, and it
 * necessarily REPAINTS the surface: a white-background lockup turns a near-black
 * end card white. Everything drawn on that card has to follow, or the fix trades
 * an invisible logo for invisible copy — which is what a first render of nafi
 * aesthetics did, plate correct and every line of type gone.
 *
 * With no scrim the surface keeps its authored colour, and in every document
 * that binds this role that colour is dark — so the fallback is the near-white
 * those cards already used. It is a constant rather than a lookup because the
 * plate's own colour is authored per format and a Theme cannot see it; if a
 * LIGHT-plated format ever binds this role, it wants its own literal instead.
 */
const END_CARD_INK_ON_DARK = '#F3EEE9';
/**
 * How much of a mark may be dark before the plate under it has to go light.
 *
 * A third. Below that, a few dark accents on an otherwise light mark are a
 * design detail and repainting the whole card for them would be worse than
 * leaving them. At or above it, the mark's substance is dark and on a dark
 * plate it is not there.
 *
 * Chosen against the measured corpus: the marks that read run 0-8% dark, the
 * ones that vanish run 52-100% (ClearSkin4u 52%, The Skin Sanctuary 79%, Skin
 * Bliss 100%), and nothing sits between 8% and 52%. A third puts the line in
 * that gap rather than next to either group.
 */
const INK_DARK_SHARE_NEEDING_LIGHT = 0.33;
/**
 * How far from square a mark may be and still be CONTINUED as a surface.
 *
 * The plate is 0.5625:1 portrait. Within roughly a third to three times square,
 * a cover-fill still shows recognisably the same artwork; a 7.9:1 banner or a
 * tall column does not. Deliberately generous — this only has to exclude the
 * cases where the fill degenerates into magnified noise.
 */
const BACKDROP_MIN_ASPECT = 0.33;
const BACKDROP_MAX_ASPECT = 3;
/**
 * WHAT COLOUR THE PLATE BEHIND A BRAND MARK SHOULD BE — the single decision,
 * so the surface and the ink drawn on it cannot disagree.
 *
 * That split is not hypothetical: the scrim rule shipped as one rule for the
 * surface and baked literals for the ink, and the first render of an opaque
 * mark had a perfect plate with every line of copy invisible on it. Both
 * `resolveFrame` and `onLogoScrimColor` now call THIS.
 *
 * Three cases, in order:
 *
 *   1. The mark carries its own background — take it, and the rectangle has no
 *      edge to show.
 *   2. No background, but the ink is too dark to read on the plate the format
 *      authored — go light, because a transparent mark laid on a dark surface
 *      with dark ink is simply absent.
 *   3. Otherwise null: the format keeps its own colour, which is the behaviour
 *      that predates any of this and is right for the marks already drawn light.
 */
export function logoPlateColor(theme) {
    const { scrim, inkDarkShare, meanColor, backgroundColor, usesTransparency } = theme.logo;
    if (scrim)
        return scrim;
    // AN AVERAGED SCRIM, for artwork that brings a whole scene.
    //
    // 9 of 61 marks are opaque and have no agreed border: a marble banner with
    // the wordmark set over it, a logo composited onto a green gradient as a
    // presentation mockup. They paint a rectangle wherever they land and there is
    // no single surround to adopt — so the mean stands in for one. The plate
    // lands on the artwork's own value and hue, and the edge stops reading as an
    // edge even though the texture does not continue across it.
    //
    // Only when OPAQUE. On a transparent mark the mean is the ink, and a plate
    // that colour would hide the mark completely rather than reveal it.
    // The BACKGROUND, not the mean. A white card with a gold brushstroke and
    // black type averages to khaki — painting the plate that average put a white
    // rectangle on a khaki ground and made the box MORE obvious than the dark
    // plate it replaced. `meanColor` stays as the fallback for artwork the probe
    // could not find a background in.
    const surface = backgroundColor ?? meanColor;
    if (usesTransparency === false && surface)
        return surface;
    if (inkDarkShare === null)
        return null;
    if (inkDarkShare < INK_DARK_SHARE_NEEDING_LIGHT)
        return null;
    // A transparent dark mark needs a light surface. Prefer the business's own
    // light background, including white; a dark brand surface cannot help it.
    return contrastRatio(theme.colors.surface, '#000000') >= 7
        ? theme.colors.surface
        : '#FFFFFF';
}
/**
 * THE MARK'S OWN PIXELS, to be continued across the surface behind it — or null
 * when a flat plate is the right answer.
 *
 * The finish for the case `logoPlateColor` can only approximate. An opaque mark
 * with no agreed border is artwork carrying a whole scene, and against a flat
 * plate its rectangle keeps a visible edge no matter how well the colour is
 * matched: a two-colour gradient sits a long way from its own mean. Blurred and
 * cover-filled, the plate becomes a continuation of the same image and there is
 * no edge left to see.
 *
 * Returns null for every mark the flat rules already handle:
 *   · a mark WITH a scrim — the plate takes that colour exactly, and a blur
 *     would only soften an edge that has already gone;
 *   · a TRANSPARENT mark — there is no rectangle, and filling the plate with
 *     the mark's own ink would swallow it.
 *
 * `logoPlateColor` still supplies the flat colour underneath, so a backdrop
 * that fails to load degrades to the averaged scrim rather than to nothing.
 */
export function logoPlateBackdrop(theme) {
    const { scrim, usesTransparency, light, aspect, backgroundColor } = theme.logo;
    if (scrim)
        return null;
    if (usesTransparency !== false)
        return null;
    // ONLY WHEN THE ARTWORK HAS NO FLAT BACKGROUND, and the failure that taught
    // this is worth keeping. Embody's mark is a white card with a large dark-teal
    // medallion on it. Its background is the card, so the plate went light and the
    // ink went dark — but a blurred cover-fill magnifies the MEDALLION, not the
    // card, and the surface rendered dark teal under dark ink.
    //
    // A blur of a flat card with a mark on it shows the MARK. It continues a
    // SURFACE only when the surface is what fills the frame — a gradient, a
    // photograph — which is exactly the case where no colour dominates. Where one
    // does, the flat plate is both correct and the colour the ink was chosen for.
    if (backgroundColor !== null)
        return null;
    // THE SHAPE HAS TO BE WITHIN REACH OF THE PLATE'S, or the fill shows a
    // fragment rather than the artwork.
    //
    // A blurred cover-fill scales the image until it covers the plate. Citrus and
    // Sage's mark is a 7.9:1 banner and the plate is 0.5625:1 — a fourteenfold
    // mismatch, so a strip a few pixels tall is magnified across the whole frame.
    // Rendered, that is a formless olive smear: not the artwork, and not even its
    // colour, drifting from a mean luminance of 0.029 to 0.084 because the crop
    // that survives is a lighter fragment than the whole.
    //
    // Outside this range the flat averaged scrim is the better answer, and the
    // safer one: it IS the whole-image average, which is what the ink was chosen
    // for.
    if (aspect === null)
        return null;
    if (aspect < BACKDROP_MIN_ASPECT || aspect > BACKDROP_MAX_ASPECT)
        return null;
    return light ?? null;
}
export function onLogoScrimColor(theme) {
    const plate = logoPlateColor(theme);
    return plate ? onAccentColor(plate) : END_CARD_INK_ON_DARK;
}
//# sourceMappingURL=theme.js.map